import { call, put, select, takeEvery } from 'redux-saga/effects';
import Cookies from 'universal-cookie';
import { appInitAction, sendFacebookEventAction, setIsFirstLoginAction, } from '../../app/appActions';
import { FEATURES, RouterPaths } from "../../app/appConstants";
import appHistory from '../../app/appHistory';
import { appInitStartSaga, checkPasswordComplexitySaga } from '../../app/appSagas';
import { getAppFeatures, getAppUser, getIsFirstLoginSelector, } from "../../app/appSelectors";
import { failurePut } from '../../app/failurePut';
import { postRequest } from '../../app/request';
import { IFeatures } from '../../app/types/appActions.t';
import { isClientMobile } from "../../components/ClientInfo/ClientInfo";
import { isEmailValid } from "../../utils/escapeUtils";
import { pushGtmEventAction } from "../GoogleTagManager/googleTagManagerActions";
import {
    autoLoginFailure,
    autoLoginSuccess,
    checkCodeFailure,
    checkCodeSuccess,
    completeMobileLoginStateAction,
    createPasswordFailure,
    createPasswordSuccess,
    loginFailure,
    loginSuccess,
    loginWithGoogleAction,
    loginWithTelegramStop,
    logoutFailure,
    logoutSuccess,
    passwordResetFailure,
    passwordResetSuccess,
    passwordUpdateFailure,
    passwordUpdateSuccess,
    registrationAction,
    registrationFailure,
    registrationSuccess,
    resendCodeFailure,
    resendCodeSuccess,
    setChangeEmailSuccess,
    setRegistrationDataAction,
} from './loginActions';
import {
    AUTO_LOGIN_START,
    CHECK_CODE_START,
    COMPLETE_MOBILE_LOGIN_STATE_START,
    CREATE_PASSWORD_START,
    LOGIN_START,
    LOGIN_WITH_GOOGLE_START,
    LOGIN_WITH_TELEGRAM_START,
    LOGOUT_START,
    LOGOUT_SUCCESS,
    MOBILE_LOGIN_STATES,
    PASSWORD_RESET_START,
    PASSWORD_UPDATE_START,
    REGISTRATION_PREPARE,
    REGISTRATION_START,
    REGISTRATION_SUCCESS,
    RESEND_CODE_START,
} from './loginConstants';
import { IAuthResponseData, IFormError, IRegistation } from './types/loginActions';
import timeout from "../../utils/timeout";
import { setIsFirstShowIntroAction, tutorialsSlice } from "../Tutorials/tutorialsReducer";
import { locationOpenOriginPageSaga } from "../Location/locationSagas";
import { GTM_EVENTS } from "../GoogleTagManager/googleTagManagerConstants";
import { animationSlice } from "../Animation/animationReducer";
import { ANIMATION_EVENT_NAMES } from "../Animation/animationConstants";
import { FB_EVENTS } from "../../utils/sendFacebookEvent";
import moment from "moment";
import base64 from "base-64";
import { assetTabsSlice } from "../AssetTabs/assetTabsReducer";
import { REFERRAL_REF_CODE_ATTRIBUTE } from "../Referral/referralConstants";
import { locationSlice } from "../Location/locationReducer";
import { LOCATION_NAMES } from "../Location/locationConstants";
import { getIsLocationPlatformMobileSelector } from "../Location/locationSelectors";
import { closeNotificationsWebsocketForReconnectSaga } from "../Notifications/notificationsSagas";
import getIsTgAuth from "../../providers/TelegramProvider/utils/getIsTgAuth";
import { academySlice } from "../Academy/academyReducer";

const browserCookies = new Cookies();

interface IUpdateCookiesOnAuthSaga {
    payload: {
        token: string;
        secret: string;
    },
}

export function updateCookiesOnAuthSaga({ payload }: IUpdateCookiesOnAuthSaga) {
    browserCookies.set('token', payload.token, { path: '/', maxAge: 60 * 60 * 24 * 365 });
    browserCookies.set('secret', payload.secret, { path: '/', maxAge: 60 * 60 * 24 * 365 });
    browserCookies.remove('pin', { path: '/' });
    browserCookies.remove('pinOk', { path: '/' });
    browserCookies.remove('pinCodeAttempts', { path: '/' });
}

interface ILoginStartSaga {
    payload: {
        email: string;
        onFailureCallback: (str: string) => void;
        onSuccessCallback: () => void;
        password: string;
        telegramInitData?: any,
    }
}

export function* loginStartSaga({ payload }: ILoginStartSaga) {
    try {
        yield call(postRequest, '/user/login', payload);

        yield put(loginSuccess());
        payload.onSuccessCallback && payload.onSuccessCallback();
        yield put(appInitAction());
    } catch (e: any) {
        yield call(failurePut, e, loginFailure);
        payload?.onFailureCallback && payload.onFailureCallback(e.response?.data?.messageCode);
    }
}

export function* loginWithGoogleSaga({ payload }: ReturnType<typeof loginWithGoogleAction>) {
    try {
        const data: IAuthResponseData = yield call(postRequest, '/auth/google/login', payload) || {};

        yield put(loginSuccess());
        yield put(appInitAction());
        if (data?.registered) {
            yield put(completeMobileLoginStateAction({ state: MOBILE_LOGIN_STATES.REGISTRATION_SUCCESS }));
        }
    } catch (e: any) {
        yield call(failurePut, e, loginFailure);
        payload?.onFailureCallback && payload.onFailureCallback(e.response?.data?.messageCode);
    }
}

export function* loginWithTelegramSaga() {
    try {
        const data: IAuthResponseData = yield call(postRequest, '/auth/telegram', {
            telegramInitData: window?.Telegram?.WebApp?.initData ?? '',
        }) || {};
        yield put(loginSuccess());
        yield put(appInitAction());
        if (data?.registered) {
            yield put(completeMobileLoginStateAction({ state: MOBILE_LOGIN_STATES.REGISTRATION_SUCCESS }));
        }
    } catch (e: any) {
        yield put(loginWithTelegramStop());
    }
}

export function* loginSuccessSaga(payload: ReturnType<typeof loginSuccess>) {
    const userProfile = yield select(getAppUser);
    localStorage.setItem('lastlogin_data', base64.encode(JSON.stringify({
        email: userProfile?.email,
        updated_at: moment().toISOString(),
    })));
}

interface ILogoutStartSaga {
    payload?: {
        source: string;
    }
}

export function* logoutStartSaga({ payload }: ILogoutStartSaga) {
    try {
        yield call<(url: string) => void>(postRequest, '/user/logout');
    } catch (e: any) {
        yield call(failurePut, e, logoutFailure);
    } finally {
        browserCookies.remove('token', { path: '/' });
        browserCookies.remove('fp', { path: '/' });
        browserCookies.remove('secret', { path: '/' });
        browserCookies.remove('credential_id', { path: '/' });
        browserCookies.remove('pin', { path: '/' });
        browserCookies.remove('pinOk', { path: '/' });
        browserCookies.remove('pinCodeAttempts', { path: '/' });
        yield put(pushGtmEventAction({ event: 'logout', source: payload?.source || null }));
        yield put(logoutSuccess());
        yield put(assetTabsSlice.actions.resetInitialAction());
        appHistory.push(RouterPaths.login);
        yield call(appInitStartSaga);
        yield call(closeNotificationsWebsocketForReconnectSaga);
    }
}

interface IValidateRegisterDataSaga {
    payload: IRegistation;
}

export function* validateRegisterDataSaga({ payload }: IValidateRegisterDataSaga) {
    if (!payload?.email) {
        throw { messageCode: 'error.email_required' };
    }
    if (!isEmailValid(payload?.email)) {
        throw { messageCode: 'error.email_not_valid' };
    }
    const features: IFeatures = yield select(getAppFeatures);
    if (features.register_without_verify) {

        const passwordComplexityError: string = yield call<typeof checkPasswordComplexitySaga>(checkPasswordComplexitySaga, payload.password);
        if (passwordComplexityError) {
            throw { messageCode: passwordComplexityError }
        }
    }
    if (!payload.acceptRules) {
        throw { messageCode: 'error.policy_not_accepted' };
    }
}

interface IRegistrationPrepareSaga {
    payload: IRegistation
}

export function* registrationPrepareSaga({ payload }: IRegistrationPrepareSaga) {
    try {
        yield call<({ payload }: IValidateRegisterDataSaga) => void>(validateRegisterDataSaga, { payload });
        const features: IFeatures = yield select(getAppFeatures);
        if (features?.register_without_verify) {
            yield put(setRegistrationDataAction(payload));
            yield call(postRequest, '/user/register-check', payload);
            yield put(registrationAction(payload));
        } else {
            yield put(registrationAction(payload));
        }
    } catch (e: any) {
        yield call(failurePut, e, registrationFailure);
    }
}

interface IRegistrationStartSaga {
    payload: IRegistation;
}

export function* registrationStartSaga({ payload }: IRegistrationStartSaga) {
    try {
        if (payload.country) {
            browserCookies.set('country', payload.country, { path: '/', maxAge: 60 * 60 * 24 * 365 });
        }
        if (!isEmailValid(payload.email)) {
            throw { messageCode: 'error.email_not_valid' };
        }
        const locale = browserCookies.get('locale');
        const data = yield call(postRequest, '/user/register', {
            email: payload.email,
            phone: payload.phone,
            lang: locale,
            country: payload.country,
            password: payload.password,
        });
        yield put(registrationSuccess(payload));
        yield put(setChangeEmailSuccess(false));
        yield put(pushGtmEventAction({ event: GTM_EVENTS.SIGN_UP_COMPLETED }));

        if (data?.token) {
            localStorage.setItem('sessionToken', data.token);
        }

        if (data?.token) {
            yield call(completeRegistrationSaga, data);
        } else {
            appHistory.push(`${RouterPaths.confirm}?register-email=${encodeURIComponent(payload.email)}`);
        }
    } catch (e: any) {
        yield call(failurePut, e, registrationFailure);
    }
}

export function* registrationSuccessSaga({ payload }: ReturnType<typeof registrationSuccess>) {
    try {
        const fp = browserCookies.get('fp');
        yield put(sendFacebookEventAction({
            event: FB_EVENTS.SIGNUP,
            eventID: fp,
            data: {},
        }));
    } catch (e: any) {
        // do nothing
    }
}

interface ICiheckCodeSaga {
    payload: {
        code: string;
        email: string;
        confirmEmail: string;
        onSuccess: () => void;
        failureRedirectLink: string;
    }
}

export function* checkCodeSaga({ payload }: ICiheckCodeSaga) {
    try {
        if (!payload.code || !payload.email) {
            throw {
                messageCode: 'error.check_inputs'
            };
        }
        const url = payload.confirmEmail ? '/user/confirm-email' : '/user/check';
        yield call(postRequest, url, {
            email: payload.email,
            code: payload.code,
        });
        yield put(checkCodeSuccess());
        if (payload.onSuccess) {
            payload.onSuccess();
        }
    } catch (e: any) {
        yield call(failurePut, e, checkCodeFailure);
        if (payload.failureRedirectLink) {
            appHistory.push(payload.failureRedirectLink)
        }
    }
}

interface ICreatePasswordSaga {
    payload: {
        email: string;
        password: string;
        password2: string;
        code: string;
    }
}

export function* createPasswordSaga({ payload }: ICreatePasswordSaga) {
    try {
        const passwordComplexityError: IFormError = yield call(checkPasswordComplexitySaga, payload.password);
        if (passwordComplexityError) {
            yield put(registrationFailure(passwordComplexityError));
            return;
        }
        if (payload.password !== payload.password2) {
            yield put(registrationFailure('promo.fx.new_password.text2'));
            return;
        }
        const data: ICompleteRegistrationSaga = yield call(postRequest, '/user/password/create', {
            email: payload.email,
            code: payload.code,
            password: payload.password
        });
        yield call(completeRegistrationSaga, data);
        yield put(createPasswordSuccess());
    } catch (e: any) {
        yield call(failurePut, e, createPasswordFailure);
    }
}


interface ICompleteRegistrationSaga {
    token: string;
    secret: string;
}

export function* completeRegistrationSaga(data: ICompleteRegistrationSaga) {
    yield call(updateCookiesOnAuthSaga, { payload: { token: data.token, secret: data.secret } });
    yield put(setIsFirstLoginAction(true));
    if (!isClientMobile) {
        yield put(setIsFirstShowIntroAction(true));
    }
    yield call(appInitStartSaga);
}

interface IPasswordResetSaga {
    payload: {
        email: string;
    }
}

export function* passwordResetSaga({ payload }: IPasswordResetSaga) {
    try {
        yield call(postRequest, '/user/password/reset', {
            email: payload.email,
        });
        yield put(passwordResetSuccess());
    } catch (e: any) {
        yield call(failurePut, e, passwordResetFailure);
    }
}

interface IPasswordUpdateSaga {
    payload: {
        email: string;
        password: string;
        password2: string;
        code: string;
        onSuccessCallback?: () => void

    }
}

export function* passwordUpdateSaga({ payload }: IPasswordUpdateSaga) {
    try {
        if (payload.email.length < 4 || payload.code.length < 4) {
            yield put(registrationFailure('error.password_url'));
            return;
        }
        const passwordComplexityError: IFormError = yield call(checkPasswordComplexitySaga, payload.password);
        if (passwordComplexityError) {
            yield put(registrationFailure(passwordComplexityError));
            return;
        }
        if (payload.password !== payload.password2) {
            yield put(registrationFailure('promo.fx.new_password.text2'));
            return;
        }
        const data: { token: string, secret: string; } = yield call(postRequest, '/user/password/update', {
            email: payload.email,
            code: payload.code,
            password: payload.password,
        });
        yield call(updateCookiesOnAuthSaga, { payload: { token: data.token, secret: data.secret } });
        yield put(passwordUpdateSuccess());
        yield put(appInitAction());
        payload.onSuccessCallback && payload.onSuccessCallback()
    } catch (e: any) {
        yield call(failurePut, e, passwordUpdateFailure);
    }
}

interface IResendCodeSaga {
    payload: {
        email: string;
        requestType: string;
    }
}

export function* resendCodeSaga({ payload }: IResendCodeSaga) {
    try {
        const { email, requestType } = payload;
        yield call(postRequest, '/user/resend', { email, requestType });
        yield put(resendCodeSuccess());
    } catch (e: any) {
        yield call(failurePut, e, resendCodeFailure);
    }
}

interface IAutoLoginSaga {
    payload: {
        email: string;
        code: string;
    }
}

export function* autoLoginSaga({ payload }: IAutoLoginSaga) {
    try {
        const data: { token: string, secret: string } = yield call(postRequest, '/user/password/create', {
            email: payload.email,
            code: payload.code,
        });
        yield call(updateCookiesOnAuthSaga, { payload: { token: data.token, secret: data.secret } });
        yield put(setIsFirstLoginAction(true));
        yield call(appInitStartSaga);
        yield put(autoLoginSuccess());
        appHistory.push(`${RouterPaths.cashier}`);
    } catch (e: any) {
        yield call(failurePut, e, autoLoginFailure);
        appHistory.push(RouterPaths.brokenLink);
    }
}

export function* completeMobileLoginStateSaga({ payload }: ReturnType<typeof completeMobileLoginStateAction>) {
    const isFirstLogin = yield select(getIsFirstLoginSelector);
    const features = yield select(getAppFeatures);
    const isMobilePlatform = yield select(getIsLocationPlatformMobileSelector);
    const isTgAuth = getIsTgAuth();

    switch (payload.state) {
        case MOBILE_LOGIN_STATES.REGISTRATION_SUCCESS:
            yield put(setIsFirstLoginAction(true));
            if (isMobilePlatform && !isTgAuth) {
                // do nothing, /auth page will continue flow
            } else {
                yield put(completeMobileLoginStateAction({ state: MOBILE_LOGIN_STATES.REFERRAL_AMAZING }));
            }
            break;

        case MOBILE_LOGIN_STATES.PIN_FINGERPRINT:
            yield put(completeMobileLoginStateAction({ state: MOBILE_LOGIN_STATES.REFERRAL_AMAZING }));
            break;

        case MOBILE_LOGIN_STATES.REFERRAL_AMAZING:
            const isFeatureReferralEnabled = !!features[FEATURES.REFERRAL];

            if (isFirstLogin && isFeatureReferralEnabled && browserCookies.get(REFERRAL_REF_CODE_ATTRIBUTE)) {
                yield put(locationSlice.actions.openLocationAction({ name: LOCATION_NAMES.REFERRAL_AMAZING }));
            } else {
                yield put(completeMobileLoginStateAction({ state: MOBILE_LOGIN_STATES.HOME }));
            }
            break;

        case MOBILE_LOGIN_STATES.HOME:
            yield call(locationOpenOriginPageSaga);
            yield call(timeout, 0);
            if (isFirstLogin) {
                yield put(animationSlice.actions.setIsShowAnimationOnEventAction({
                    eventName: ANIMATION_EVENT_NAMES.FIRST_START,
                    isShow: isMobilePlatform,
                }));
                yield put(academySlice.actions.setIsShowBeginnersGuideInviteAction(true));

                // TODO CFD-10290 Удалить после интеграции академии
                yield put(tutorialsSlice.actions.setIsShowBeginnersGuideInviteAction(true));
            }
            break;

        default:
            break;
    }
}

export function* loginSagas() {
    //@ts-ignore
    yield takeEvery(LOGIN_START, loginStartSaga);
    yield takeEvery(LOGIN_WITH_GOOGLE_START, loginWithGoogleSaga);
    yield takeEvery(LOGIN_WITH_TELEGRAM_START, loginWithTelegramSaga);
    yield takeEvery(LOGOUT_SUCCESS, loginSuccessSaga);
    //@ts-ignore
    yield takeEvery(LOGOUT_START, logoutStartSaga);
    //@ts-ignore
    yield takeEvery(PASSWORD_RESET_START, passwordResetSaga);
    //@ts-ignore
    yield takeEvery(PASSWORD_UPDATE_START, passwordUpdateSaga);
    //@ts-ignore
    yield takeEvery(REGISTRATION_START, registrationStartSaga);
    //@ts-ignore
    yield takeEvery(REGISTRATION_PREPARE, registrationPrepareSaga);
    yield takeEvery(REGISTRATION_SUCCESS, registrationSuccessSaga);
    //@ts-ignore
    yield takeEvery(CHECK_CODE_START, checkCodeSaga);
    //@ts-ignore
    yield takeEvery(CREATE_PASSWORD_START, createPasswordSaga);
    //@ts-ignore
    yield takeEvery(RESEND_CODE_START, resendCodeSaga);
    //@ts-ignore
    yield takeEvery(AUTO_LOGIN_START, autoLoginSaga);
    yield takeEvery(COMPLETE_MOBILE_LOGIN_STATE_START, completeMobileLoginStateSaga);
}
